"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-6">
        <div className="h-3 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-3 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-3 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-3 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-0">
        {menuItems.map((item, i) => <NavigationMenuItem data-index={i} key={item.id} className="flex items-center">
            <Link to={item.href || "#"} className="text-foreground text-xs uppercase tracking-[0.2em] font-default px-4 py-2 hover:text-primary transition-colors duration-300">
              {item.label}
            </Link>
            {i < menuItems.length - 1 && <span aria-hidden="true" className="h-3 w-px bg-border" />}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-6">
        <div className="h-10 w-48 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-56 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-44 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-52 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-6 px-6 pt-4">
      {menuItems.map((item, i) => <Link data-index={i} key={item.id} to={item.href || "#"} onClick={onNavigate} className="text-foreground font-default italic text-4xl tracking-tight hover:text-primary transition-colors duration-300" style={{
      animation: `fadeSlideIn 0.5s ease-out ${i * 80}ms both`
    }}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function MainNavigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 30);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="4725" id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-500 bg-background ${scrolled ? "backdrop-blur-lg border-b border-border" : "border-b border-transparent"}`}>
      <style>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      <div className={`mx-auto max-w-7xl px-6 lg:px-10 flex items-center justify-between transition-all duration-500 ${scrolled ? "py-5" : "py-8"}`}>
        <Link to="/" className="flex items-center" aria-label="Peak Force Fitness home">
          <WvcLogo className="h-7 w-auto" />
        </Link>

        <div className="hidden lg:flex items-center">
          <MenuProvider menu_id="2">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="hidden lg:flex items-center">
          <Button asChild variant="outline" className="rounded-lg border border-primary bg-transparent text-primary hover:bg-primary hover:text-background font-default italic text-sm px-6 py-2.5 h-auto transition-colors duration-300">
            <Link to="/JoinTheForcePage">Start Your Journey</Link>
          </Button>
        </div>

        <div className="flex lg:hidden items-center">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" className="text-foreground hover:bg-accent rounded-lg">
                <Menu className="size-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-background border-l border-border w-full sm:max-w-md p-0">
              <SheetHeader className="px-6 pt-6 pb-4">
                <SheetTitle className="font-default italic text-2xl text-foreground text-left">
                  <WvcLogo className="h-7 w-auto" />
                </SheetTitle>
              </SheetHeader>

              <div className="flex flex-col h-full">
                <MenuProvider menu_id="2">
                  <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                </MenuProvider>

                <div className="mt-auto px-6 pb-10 pt-8">
                  <Button asChild variant="outline" className="w-full rounded-lg border border-primary bg-transparent text-primary hover:bg-primary hover:text-background font-default italic text-base py-3 h-auto" onClick={() => setMobileOpen(false)}>
                    <Link to="/JoinTheForcePage">Start Your Journey</Link>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}